"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useMediaQuery } from "@mui/material";
import ReactMarkdown from "react-markdown";
import { toast, ToastContainer } from "react-toastify";
import {
  Menu,
  UploadCloud,
  FileText,
  Send,
  Loader2,
  CheckCircle2,
  Trash2,
} from "lucide-react";
import "./document-analysis.css";
import modelLogo from "../../assets/images/modelLogo.png";
import {
  uploadDocumentAnalysisFileApi,
  documentAnalysisChatStreamApi,
  generateDocumentAnalysisDocxApi,
  getDocumentAnalysisRecommendationsApi,
} from "../../services/api/documentAnalysisApi";
import { searchStatutesApi } from "../../services/api/statuteSearchApi";
import { parseSSEStream, extractBlobErrorMessage } from "../../utils/HelperFunction/utils";
import {
  DOCUMENT_ANALYSIS_SESSIONS_KEY,
  DOCUMENT_ANALYSIS_ACTIVE_SESSION_KEY,
} from "../../utils/HelperFunction/Constants";
import { findCaseFromAzureAiSearch } from "../../actions/searchActions";
import ConfirmationDialog from "../../dialogs/ConfirmationDialog";
import CardContentPanelDialog from "../../dialogs/CardContentPanelDialog";
import CaseDetailDialog from "../../dialogs/CaseDetailDialog";
import StatuteContentDialog from "../../dialogs/StatuteContentDialog";

const genId = () => crypto.randomUUID();

const CARD_LABELS = {
  summarize: "Summary",
  legality: "Legality Review",
  bias: "Bias Analysis",
  template: "Template Comparison",
};

const prettyCardLabel = (cardType) =>
  CARD_LABELS[cardType] ||
  (cardType || "Document")
    .replace(/[_-]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const stripExtension = (filename) => (filename || "Document").replace(/\.[^./]+$/, "");

// A card's text payload sometimes comes wrapped in a <document>...</document>
// tag from the backend — strip it so it doesn't render as literal text.
const cleanCardText = (text) =>
  (text || "").replace(/^\s*<document>\s*/i, "").replace(/\s*<\/document>\s*$/i, "");

// Determines how a card's data should be rendered in the side panel: as a
// list of case/statute reference rows, or as plain markdown text. The text
// field name varies per card type (summary, result, improved_document, ...),
// so fall back to the first string value found rather than a fixed key list.
const resolveCardContent = (card) => {
  const data = card?.data || {};
  if (Array.isArray(data.cases) && data.cases.length) {
    return { kind: "cases", items: data.cases };
  }
  if (Array.isArray(data.statutes) && data.statutes.length) {
    return { kind: "statutes", items: data.statutes };
  }
  if (Array.isArray(data.references) && data.references.length) {
    return { kind: "references", items: data.references };
  }
  if (Array.isArray(data) && data.length) {
    return { kind: "references", items: data };
  }
  if (typeof data === "string") {
    return { kind: "text", text: cleanCardText(data) };
  }
  const firstStringValue = Object.values(data).find((v) => typeof v === "string" && v.trim());
  if (firstStringValue) {
    return { kind: "text", text: cleanCardText(firstStringValue) };
  }
  // Some cards (e.g. counter_arguments) hold their payload as an array of
  // plain strings rather than reference objects — render those as a markdown list.
  const firstStringArray = Object.values(data).find(
    (v) => Array.isArray(v) && v.length && v.every((item) => typeof item === "string")
  );
  return { kind: "text", text: cleanCardText(firstStringArray?.map((item) => `- ${item}`).join("\n\n")) };
};

// ─── Session storage (one session per uploaded document) ─────────────────────
function useDocumentAnalysisSessions() {
  const [sessions, setSessions] = useState(() => {
    try {
      const stored = localStorage.getItem(DOCUMENT_ANALYSIS_SESSIONS_KEY);
      const parsed = stored ? JSON.parse(stored) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  const [activeSessionId, setActiveSessionId] = useState(() => {
    try {
      return localStorage.getItem(DOCUMENT_ANALYSIS_ACTIVE_SESSION_KEY) || null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(DOCUMENT_ANALYSIS_SESSIONS_KEY, JSON.stringify(sessions));
    } catch {
      // ignore quota errors
    }
  }, [sessions]);

  useEffect(() => {
    try {
      if (activeSessionId) {
        localStorage.setItem(DOCUMENT_ANALYSIS_ACTIVE_SESSION_KEY, activeSessionId);
      } else {
        localStorage.removeItem(DOCUMENT_ANALYSIS_ACTIVE_SESSION_KEY);
      }
    } catch {
      // ignore
    }
  }, [activeSessionId]);

  const createSession = useCallback((session) => {
    setSessions((prev) => [session, ...prev]);
  }, []);

  const deleteSession = useCallback((id) => {
    setSessions((prev) => prev.filter((s) => s.id !== id));
  }, []);

  const getSession = useCallback((id) => sessions.find((s) => s.id === id), [sessions]);

  const setSessionMessages = useCallback((sessionId, messages) => {
    setSessions((prev) =>
      prev.map((s) => (s.id === sessionId ? { ...s, messages, updatedAt: Date.now() } : s))
    );
  }, []);

  const patchMessage = useCallback((sessionId, messageId, patch) => {
    setSessions((prev) =>
      prev.map((s) => {
        if (s.id !== sessionId) return s;
        return {
          ...s,
          messages: s.messages.map((m) => (m.id === messageId ? { ...m, ...patch(m) } : m)),
        };
      })
    );
  }, []);

  return {
    sessions,
    activeSessionId,
    setActiveSessionId,
    createSession,
    deleteSession,
    getSession,
    setSessionMessages,
    patchMessage,
  };
}

// ─── Thinking dots ────────────────────────────────────────────────────────────
function ThinkingDots() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          style={{
            width: "6px",
            height: "6px",
            borderRadius: "50%",
            background: "#9ca3af",
            display: "inline-block",
            animation: "daDot 0.9s infinite",
            animationDelay: `${i * 0.15}s`,
          }}
        />
      ))}
      <style>{`@keyframes daDot{0%,80%,100%{transform:translateY(0)}40%{transform:translateY(-5px)}}`}</style>
    </div>
  );
}

// ─── Clickable analysis card ──────────────────────────────────────────────────
function DocCard({ card, onClick }) {
  return (
    <div className="da-card" onClick={onClick}>
      <div className="icon">
        <FileText size={16} />
      </div>
      <div className="text">
        <p className="label">{prettyCardLabel(card.cardType)}</p>
        <p className="hint">Click to view</p>
      </div>
    </div>
  );
}

// ─── Message bubble ───────────────────────────────────────────────────────────
function MessageBubble({ message, isStreaming, onCardClick }) {
  if (message.role === "user") {
    return (
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "20px" }}>
        <div
          style={{
            background: "#f3f4f6",
            color: "#111827",
            padding: "12px 16px",
            borderRadius: "18px 18px 4px 18px",
            fontSize: "14px",
            lineHeight: "1.6",
            maxWidth: "65%",
          }}
        >
          {message.content}
        </div>
      </div>
    );
  }

  const showThinking = isStreaming && !message.content && !message.statusMessage;

  return (
    <div style={{ display: "flex", gap: "12px", marginBottom: "20px" }}>
      <div
        style={{
          width: "36px",
          height: "36px",
          borderRadius: "50%",
          flexShrink: 0,
          background: "#ffffff",
          border: "1px solid #e2e8f0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "4px",
        }}
      >
        <img src={modelLogo} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
      </div>
      <div
        style={{
          background: "#ffffff",
          color: "#374151",
          padding: "12px 16px",
          borderRadius: "4px 18px 18px 18px",
          fontSize: "14px",
          lineHeight: "1.6",
          border: "1px solid #f3f4f6",
          maxWidth: "78%",
          minWidth: 0,
        }}
      >
        {message.tools?.length > 0 && (
          <div>
            {message.tools.map((t) => (
              <span key={t.tool_name} className={`da-tool-chip ${t.status === "done" ? "done" : ""}`}>
                {t.status === "done" ? <CheckCircle2 size={12} /> : <Loader2 size={12} className="da-spin" />}
                {t.label}
              </span>
            ))}
          </div>
        )}

        {message.statusMessage && !message.content && (
          <p className="da-status-text">{message.statusMessage}</p>
        )}

        {showThinking ? <ThinkingDots /> : <ReactMarkdown>{message.content}</ReactMarkdown>}

        {message.cards?.length > 0 && (
          <div className="da-card-list">
            {message.cards.map((card) => (
              <DocCard key={card.id} card={card} onClick={() => onCardClick(message.id, card)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Upload zone ───────────────────────────────────────────────────────────────
function UploadZone({ onFile, isUploading }) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  return (
    <div className="da-upload-wrap">
      <div
        className={`da-upload-zone ${dragging ? "dragging" : ""}`}
        onClick={() => !isUploading && inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          if (!isUploading) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          if (isUploading) return;
          const file = e.dataTransfer.files?.[0];
          if (file) onFile(file);
        }}
      >
        <div className="da-upload-icon">
          {isUploading ? <Loader2 size={26} className="da-spin" color="#3C50B1" /> : <UploadCloud size={26} color="#3C50B1" />}
        </div>
        <h2 className="da-upload-title">
          {isUploading ? "Analyzing your document…" : "Upload a document to get started"}
        </h2>
        <p className="da-upload-subtitle">
          {isUploading
            ? "This may take a few seconds"
            : "Drop a file here, or click to browse. Ask questions about it once it's ready."}
        </p>
        {!isUploading && (
          <div className="da-upload-formats">
            <span className="da-format-badge">DOCX</span>
            <span className="da-format-badge">PDF</span>
            <span>· Max 20 MB</span>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.docx"
          style={{ display: "none" }}
          disabled={isUploading}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onFile(file);
            e.target.value = "";
          }}
        />
      </div>
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────
export const DocumentAnalysisParent = () => {
  const {
    sessions,
    activeSessionId,
    setActiveSessionId,
    createSession,
    deleteSession,
    getSession,
    setSessionMessages,
    patchMessage,
  } = useDocumentAnalysisSessions();

  const [isUploading, setIsUploading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [input, setInput] = useState("");
  const [showLeftPanel, setShowLeftPanel] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [panelCard, setPanelCard] = useState(null);
  const [panelDownloading, setPanelDownloading] = useState(false);
  const [openCaseInsights, setOpenCaseInsights] = useState(false);
  const [statuteModal, setStatuteModal] = useState(null);

  const dispatch = useDispatch();
  const isMobileView = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const abortControllerRef = useRef(null);
  const lastPanelCardRef = useRef(null);

  const activeSession = getSession(activeSessionId);

  if (panelCard) lastPanelCardRef.current = panelCard;
  const displayedPanelCard = panelCard || lastPanelCardRef.current;
  const resolvedPanelContent = displayedPanelCard ? resolveCardContent(displayedPanelCard.card) : null;

  useEffect(() => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  }, [activeSession?.messages]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await getDocumentAnalysisRecommendationsApi();
        if (!cancelled) setRecommendations(response.data?.recommendations || []);
      } catch {
        // recommendations are a convenience — fail silently
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleFileSelect = async (file) => {
    setIsUploading(true);
    try {
      const response = await uploadDocumentAnalysisFileApi(file);
      const data = response.data;
      const now = Date.now();
      const session = {
        id: genId(),
        filename: data.filename || file.name,
        docType: data.doc_type || "",
        pageCount: data.page_count,
        charCount: data.char_count,
        markdown: data.markdown,
        messages: [],
        createdAt: now,
        updatedAt: now,
      };
      createSession(session);
      setActiveSessionId(session.id);
      setShowLeftPanel(false);
    } catch (error) {
      const message =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        "Upload failed. Please try again.";
      toast.error(message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleNewDocument = () => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    setActiveSessionId(null);
    setShowLeftPanel(false);
  };

  const handleSelectSession = (id) => {
    setActiveSessionId(id);
    setShowLeftPanel(false);
  };

  const handleDeleteConfirm = () => {
    if (!sessionToDelete) return;
    deleteSession(sessionToDelete.id);
    if (activeSessionId === sessionToDelete.id) setActiveSessionId(null);
    setSessionToDelete(null);
  };

  const handleOpenCard = (messageId, card) => {
    setPanelCard({ messageId, card });
  };

  const handleViewCase = (caseItem) => {
    dispatch(findCaseFromAzureAiSearch(caseItem.id));
    setOpenCaseInsights(true);
  };

  const handleCloseCaseInsights = () => {
    setOpenCaseInsights(false);
    dispatch({ type: "CASE_DETAIL_RESET" });
  };

  const handleViewStatute = async (statuteItem) => {
    setStatuteModal({ label: statuteItem.label || statuteItem.title, content: "<p>Loading…</p>" });
    try {
      const response = await searchStatutesApi({
        query: "",
        sort_by: "",
        filter: `Bookmark_ID eq ${statuteItem.id}`,
        search_field_mode: "Title",
        top: 1,
        skip: 0,
        count: true,
      });
      const match = response.data?.search_results?.[0];
      setStatuteModal({
        label: match?.Statute_Name,
        content: match?.related_data?.[0]?.Statute_HTML || "<p>No content available.</p>",
      });
    } catch (error) {
      setStatuteModal(null);
      toast.error("Failed to load the statute. Please try again.");
    }
  };

  const handleCloseStatuteModal = () => setStatuteModal(null);

  const handleClosePanel = () => setPanelCard(null);

  const handlePanelDownload = async () => {
    if (!displayedPanelCard || !activeSession) return;
    const { card } = displayedPanelCard;
    setPanelDownloading(true);
    const docTitle = `${stripExtension(activeSession.filename)} - ${prettyCardLabel(card.cardType)}`;
    try {
      const response = await generateDocumentAnalysisDocxApi(card.cardType, card.data, docTitle);
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${docTitle}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      const message = await extractBlobErrorMessage(error, "Failed to generate the document. Please try again.");
      toast.error(message);
    } finally {
      setPanelDownloading(false);
    }
  };

  const handleSend = async (overrideText) => {
    const userText = (typeof overrideText === "string" ? overrideText : input).trim();
    if (!userText || isSending || !activeSession) return;

    setInput("");
    if (textareaRef.current) textareaRef.current.style.height = "auto";

    const historyForApi = [
      ...(activeSession.messages || []).map((m) => ({ role: m.role, content: m.content })),
      { role: "user", content: userText },
    ];

    const userMsg = { id: genId(), role: "user", content: userText };
    const assistantId = genId();
    const assistantMsg = {
      id: assistantId,
      role: "assistant",
      content: "",
      statusMessage: "",
      tools: [],
      cards: [],
    };

    setSessionMessages(activeSession.id, [...(activeSession.messages || []), userMsg, assistantMsg]);
    setIsSending(true);
    abortControllerRef.current = new AbortController();

    try {
      const stream = await documentAnalysisChatStreamApi(
        activeSession.markdown,
        activeSession.docType,
        historyForApi,
        abortControllerRef.current.signal
      );

      await parseSSEStream(stream, ({ type, data }) => {
        switch (type) {
          case "status":
            patchMessage(activeSession.id, assistantId, (m) => ({ statusMessage: data.message || "" }));
            (data.warnings || []).forEach((w) => toast.warn(w));
            break;
          case "tool_start":
            patchMessage(activeSession.id, assistantId, (m) => ({
              tools: [...m.tools, { tool_name: data.tool_name, label: data.label, status: "running" }],
            }));
            break;
          case "tool_end":
            patchMessage(activeSession.id, assistantId, (m) => ({
              tools: m.tools.map((t) => (t.tool_name === data.tool_name ? { ...t, status: "done" } : t)),
            }));
            break;
          case "token":
            patchMessage(activeSession.id, assistantId, (m) => ({
              content: m.content + data.text,
              statusMessage: "",
            }));
            break;
          case "card":
            patchMessage(activeSession.id, assistantId, (m) => ({
              cards: [
                ...m.cards,
                { id: genId(), cardType: data.card_type, data: data.data, file: data.file },
              ],
            }));
            break;
          case "done":
            patchMessage(activeSession.id, assistantId, (m) => ({ statusMessage: "" }));
            break;
          default:
            break;
        }
      });
    } catch (error) {
      if (error.name !== "AbortError") {
        patchMessage(activeSession.id, assistantId, (m) => ({
          content: m.content || "Something went wrong. Please try again.",
        }));
        toast.error("Something went wrong. Please try again later.");
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const messages = activeSession?.messages || [];
  const isEmpty = messages.length === 0;

  return (
    <div className="da-layout">
      <ToastContainer />

      {sessionToDelete && (
        <ConfirmationDialog
          open={!!sessionToDelete}
          onClose={() => setSessionToDelete(null)}
          onConfirm={handleDeleteConfirm}
          title="Delete Document?"
          description={`Are you sure you want to delete "${sessionToDelete.filename}" and its conversation? This cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          confirmColor="error"
        />
      )}

      {/* ── LEFT PANEL ── */}
      <div className={`da-left-panel ${showLeftPanel ? "open" : ""}`}>
        <button type="button" className="da-new-btn" onClick={handleNewDocument}>
          <UploadCloud size={17} />
          Analyze New Document
        </button>

        <div className="da-session-list">
          {sessions.length === 0 ? (
            <p className="da-session-empty">No documents analyzed yet. Upload one to get started.</p>
          ) : (
            sessions.map((session) => (
              <button
                type="button"
                key={session.id}
                className={`da-session-item ${session.id === activeSessionId ? "active" : ""}`}
                onClick={() => handleSelectSession(session.id)}
              >
                <FileText size={14} color="#9ca3af" style={{ flexShrink: 0 }} />
                <span className="name">{session.filename}</span>
                {session.docType && <span className="type-badge">{session.docType}</span>}
                <Trash2
                  size={14}
                  color="#dc2626"
                  style={{ flexShrink: 0 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSessionToDelete(session);
                  }}
                />
              </button>
            ))
          )}
        </div>
      </div>

      {/* ── CENTER PANEL ── */}
      <div className="da-center-panel">
        {activeSession ? (
          <>
            <div className="da-chat-header">
              <div className="da-mobile-header-left">
                <button className="da-mobile-menu-btn" type="button" onClick={() => setShowLeftPanel(!showLeftPanel)}>
                  <Menu size={18} />
                </button>
              </div>
              <span className="title">{activeSession.filename}</span>
              <span className="meta">
                {activeSession.pageCount ? `${activeSession.pageCount} pages · ` : ""}
                {activeSession.charCount ? `${activeSession.charCount.toLocaleString()} chars` : ""}
              </span>
              {activeSession.docType && <span className="da-doc-type-chip">{activeSession.docType}</span>}
            </div>

            <div className="da-messages">
              {isEmpty ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    gap: "10px",
                    textAlign: "center",
                  }}
                >
                  <h2 style={{ fontSize: "22px", fontWeight: 700, color: "#111827" }}>
                    How can I help you today?
                  </h2>
                  <p style={{ fontSize: "12px", color: "#6b7280" }}>
                    Ask questions about "{activeSession.filename}", or request a summary, legality review, or bias
                    check.
                  </p>
                </div>
              ) : (
                messages.map((msg) => (
                  <MessageBubble
                    key={msg.id}
                    message={msg}
                    isStreaming={isSending && msg.id === messages[messages.length - 1]?.id}
                    onCardClick={handleOpenCard}
                  />
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="da-input-bar">
              {recommendations.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    overflowX: "auto",
                    marginBottom: "8px",
                    paddingBottom: "2px",
                  }}
                >
                  {recommendations.map((rec) => (
                    <button
                      key={rec.label}
                      type="button"
                      onClick={() => handleSend(rec.query)}
                      disabled={isSending}
                      style={{
                        flexShrink: 0,
                        padding: "6px 12px",
                        borderRadius: "999px",
                        border: "1px solid #c7d2fe",
                        background: "#eef2ff",
                        color: "#3C50B1",
                        fontSize: "12px",
                        fontWeight: 600,
                        cursor: isSending ? "not-allowed" : "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {rec.label}
                    </button>
                  ))}
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-end",
                  gap: "10px",
                  padding: "10px 14px",
                  borderRadius: "16px",
                  background: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  minWidth: 0,
                  overflow: "hidden",
                }}
              >
                <textarea
                  ref={textareaRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask a question about this document…"
                  rows={1}
                  disabled={isSending}
                  style={{
                    flex: 1,
                    resize: "none",
                    background: "transparent",
                    border: "none",
                    outline: "none",
                    fontSize: "14px",
                    color: "#111827",
                    lineHeight: "1.5",
                    fontFamily: "inherit",
                    minHeight: "40px",
                    maxHeight: "160px",
                  }}
                />
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={!input.trim() || isSending}
                  style={{
                    width: "34px",
                    height: "34px",
                    borderRadius: "9px",
                    border: "none",
                    flexShrink: 0,
                    cursor: input.trim() && !isSending ? "pointer" : "not-allowed",
                    background: input.trim() && !isSending ? "#3C50B1" : "#e5e7eb",
                    color: input.trim() && !isSending ? "#ffffff" : "#9ca3af",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Send size={14} />
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="da-chat-header">
              <div className="da-mobile-header-left">
                <button className="da-mobile-menu-btn" type="button" onClick={() => setShowLeftPanel(!showLeftPanel)}>
                  <Menu size={18} />
                </button>
              </div>
              <span className="title">Document Analysis</span>
            </div>
            <UploadZone onFile={handleFileSelect} isUploading={isUploading} />
          </>
        )}
      </div>

      {showLeftPanel && <div className="da-mobile-overlay" onClick={() => setShowLeftPanel(false)} />}

      <CardContentPanelDialog
        open={!!panelCard}
        onClose={handleClosePanel}
        title={displayedPanelCard ? prettyCardLabel(displayedPanelCard.card.cardType) : ""}
        kind={resolvedPanelContent?.kind}
        items={resolvedPanelContent?.items}
        text={resolvedPanelContent?.text}
        onDownload={resolvedPanelContent?.kind === "text" ? handlePanelDownload : null}
        downloading={panelDownloading}
        onViewCase={handleViewCase}
        onViewStatute={handleViewStatute}
      />

      <CaseDetailDialog open={openCaseInsights} onClose={handleCloseCaseInsights} />

      <StatuteContentDialog
        open={!!statuteModal}
        onClose={handleCloseStatuteModal}
        label={statuteModal?.label}
        content={statuteModal?.content}
        isMobile={isMobileView}
      />
    </div>
  );
};
